import { API_URL } from '@/config';
import { formatDate } from '@/utils/dateFormat';
import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Building2, CreditCard, QrCode, Upload, Copy, Check, Clock, X, Plus, History } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getImageUrl } from '@/utils/imageUrl';

const API = `${API_URL}/api`;

const PaymentsPage = () => {
  const { user } = useOutletContext();
  const [bankDetails, setBankDetails] = useState(null);
  const [myPayments, setMyPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [copied, setCopied] = useState('');
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    amount: '',
    purpose: '',
    screenshot_url: '',
    transaction_id: '',
    payment_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [bankRes, paymentsRes] = await Promise.all([
        axios.get(`${API}/bank-details`, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } }),
        axios.get(`${API}/payments`, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } })
      ]);
      setBankDetails(bankRes.data);
      setMyPayments(paymentsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(''), 2000);
  };

  const handleScreenshotUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(`${API}/upload`, formDataUpload, {
        headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` },
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFormData(prev => ({ ...prev, screenshot_url: response.data.url }));
      toast.success('Screenshot uploaded!');
    } catch (error) {
      toast.error('Failed to upload screenshot');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.screenshot_url) {
      toast.error('Please upload payment screenshot');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/payments`, {
        ...formData,
        amount: parseFloat(formData.amount)
      }, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
      
      toast.success('Payment submitted for approval!');
      setShowPaymentForm(false);
      setFormData({
        amount: '',
        purpose: '',
        screenshot_url: '',
        transaction_id: '',
        payment_date: new Date().toISOString().split('T')[0]
      });
      loadData();
    } catch (error) {
      toast.error('Failed to submit payment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#D4AF37' }}></div>
      </div>
    );
  }

  // Mobile View
  const MobileView = () => (
    <div className="md:hidden px-4 py-4 space-y-6">
      {/* Bank Details Card */}
      <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: '#1E293B' }}>
        <div className="p-4 border-b" style={{ borderColor: '#334155', background: 'linear-gradient(135deg, #0F172A 0%, #1E3A5F 100%)' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(212, 175, 55, 0.2)' }}>
              <Building2 className="w-5 h-5" style={{ color: '#D4AF37' }} />
            </div>
            <div>
              <h2 className="font-bold text-white">Bank Details</h2>
              <p className="text-xs" style={{ color: '#94A3B8' }}>Make payments to club account</p>
            </div>
          </div>
        </div>
        
        <div className="p-4 space-y-4">
          {/* QR Code */}
          {bankDetails?.qr_code_url && (
            <div className="flex justify-center">
              <div className="w-48 h-48 bg-white rounded-xl p-2">
                <img 
                  src={getImageUrl(bankDetails.qr_code_url)}
                  alt="Payment QR Code" 
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          )}
          
          <p className="text-center text-xs" style={{ color: '#94A3B8' }}>Scan QR code to pay via UPI</p>
          
          {/* Account Details */}
          <div className="space-y-3 pt-4 border-t" style={{ borderColor: '#334155' }}>
            {[
              { label: 'Account Name', value: bankDetails?.account_name, field: 'name' },
              { label: 'Account No', value: bankDetails?.account_number, field: 'acc' },
              { label: 'IFSC Code', value: bankDetails?.ifsc_code, field: 'ifsc' },
              { label: 'Bank', value: bankDetails?.bank_name, field: 'bank' },
              { label: 'Branch', value: bankDetails?.branch_name, field: 'branch' },
            ].map((item) => (
              <div key={item.field} className="flex items-center justify-between p-3 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                <div>
                  <p className="text-xs" style={{ color: '#64748B' }}>{item.label}</p>
                  <p className="font-medium text-white text-sm">{item.value}</p>
                </div>
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={() => handleCopy(item.value, item.field)}
                  className="h-8 w-8 p-0"
                >
                  {copied === item.field ? (
                    <Check className="w-4 h-4" style={{ color: '#22C55E' }} />
                  ) : (
                    <Copy className="w-4 h-4" style={{ color: '#D4AF37' }} />
                  )}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Submit Payment Button */}
      <Button 
        onClick={() => setShowPaymentForm(true)}
        className="w-full h-14 rounded-2xl text-lg font-semibold"
        style={{ backgroundColor: '#D4AF37', color: '#0F172A' }}
      >
        <Upload className="w-5 h-5 mr-2" />
        Submit Payment Screenshot
      </Button>
      
      {/* My Payments History */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <History className="w-5 h-5" style={{ color: '#D4AF37' }} />
          <h3 className="font-semibold text-white">My Payments</h3>
        </div>
        
        {myPayments.length === 0 ? (
          <div className="text-center py-8 rounded-2xl" style={{ backgroundColor: '#1E293B' }}>
            <CreditCard className="w-12 h-12 mx-auto mb-3" style={{ color: '#64748B' }} />
            <p style={{ color: '#64748B' }}>No payments yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {myPayments.map((payment) => (
              <div key={payment.payment_id} className="p-4 rounded-2xl" style={{ backgroundColor: '#1E293B' }}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-semibold text-white">₹{payment.amount.toLocaleString()}</p>
                    <p className="text-sm" style={{ color: '#94A3B8' }}>{payment.purpose}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
                    payment.status === 'approved' ? 'bg-green-500/20 text-green-400' : 
                    payment.status === 'rejected' ? 'bg-red-500/20 text-red-400' : 
                    'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {payment.status === 'approved' && <Check className="w-3 h-3" />}
                    {payment.status === 'rejected' && <X className="w-3 h-3" />}
                    {payment.status === 'pending' && <Clock className="w-3 h-3" />}
                    {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                  </span>
                </div>
                <p className="text-xs" style={{ color: '#64748B' }}>
                  {formatDate(payment.payment_date)}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Payment Form Dialog */}
      <Dialog open={showPaymentForm} onOpenChange={setShowPaymentForm}>
        <DialogContent className="max-w-md" style={{ backgroundColor: '#1E293B', borderColor: '#334155' }}>
          <DialogHeader>
            <DialogTitle className="text-white">Submit Payment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-white">Amount (₹) *</Label>
              <Input 
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                placeholder="Enter amount"
                required
                className="bg-white/10 border-white/20 text-white"
              />
            </div>
            
            <div>
              <Label className="text-white">Purpose *</Label>
              <Input 
                value={formData.purpose}
                onChange={(e) => setFormData({...formData, purpose: e.target.value})}
                placeholder="e.g., Membership Fee, Event Contribution"
                required
                className="bg-white/10 border-white/20 text-white"
              />
            </div>
            
            <div>
              <Label className="text-white">Payment Date *</Label>
              <Input 
                type="date"
                value={formData.payment_date}
                onChange={(e) => setFormData({...formData, payment_date: e.target.value})}
                required
                className="bg-white/10 border-white/20 text-white"
              />
            </div>
            
            <div>
              <Label className="text-white">Transaction ID (Optional)</Label>
              <Input 
                value={formData.transaction_id}
                onChange={(e) => setFormData({...formData, transaction_id: e.target.value})}
                placeholder="UPI/Bank Reference Number"
                className="bg-white/10 border-white/20 text-white"
              />
            </div>
            
            <div>
              <Label className="text-white">Payment Screenshot *</Label>
              <div className="mt-2">
                {formData.screenshot_url ? (
                  <div className="relative">
                    <img 
                      src={getImageUrl(formData.screenshot_url)}
                      alt="Screenshot" 
                      className="w-full h-48 object-contain rounded-lg bg-white/5"
                    />
                    <Button 
                      type="button"
                      size="sm"
                      variant="destructive"
                      className="absolute top-2 right-2"
                      onClick={() => setFormData({...formData, screenshot_url: ''})}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-white/5" style={{ borderColor: '#64748B' }}>
                    <Upload className="w-8 h-8 mb-2" style={{ color: '#64748B' }} />
                    <span className="text-sm" style={{ color: '#64748B' }}>Click to upload screenshot</span>
                    <input 
                      type="file" 
                      accept="image/*" 
                      className="hidden" 
                      onChange={handleScreenshotUpload}
                      disabled={uploading}
                    />
                  </label>
                )}
              </div>
            </div>
            
            <Button 
              type="submit"
              disabled={submitting || uploading || !formData.screenshot_url}
              className="w-full"
              style={{ backgroundColor: '#D4AF37', color: '#0F172A' }}
            >
              {submitting ? 'Submitting...' : 'Submit Payment'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );

  // Desktop View
  const DesktopView = () => (
    <div className="hidden md:block space-y-8">
      <div>
        <h1 className="text-4xl font-bold mb-2" style={{ color: '#0F172A' }}>Payments</h1>
        <p className="text-muted-foreground">Make payments and view payment history</p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bank Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" style={{ color: '#D4AF37' }} />
              Bank Account Details
            </CardTitle>
            <CardDescription>Transfer payments to club account</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {[
              { label: 'Account Name', value: bankDetails?.account_name, field: 'name' },
              { label: 'Account Number', value: bankDetails?.account_number, field: 'acc' },
              { label: 'IFSC Code', value: bankDetails?.ifsc_code, field: 'ifsc' },
              { label: 'Bank', value: bankDetails?.bank_name, field: 'bank' },
              { label: 'Branch', value: bankDetails?.branch_name, field: 'branch' },
            ].map((item) => (
              <div key={item.field} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div>
                  <p className="text-xs text-muted-foreground">{item.label}</p>
                  <p className="font-medium">{item.value}</p>
                </div>
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={() => handleCopy(item.value, item.field)}
                >
                  {copied === item.field ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
        
        {/* QR Code */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5" style={{ color: '#D4AF37' }} />
              Scan & Pay
            </CardTitle>
            <CardDescription>Quick payment via UPI</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center">
            {bankDetails?.qr_code_url && (
              <div className="w-64 h-64 bg-white rounded-xl p-4 shadow-lg">
                <img 
                  src={getImageUrl(bankDetails.qr_code_url)}
                  alt="Payment QR Code" 
                  className="w-full h-full object-contain"
                />
              </div>
            )}
            <p className="text-center text-sm text-muted-foreground mt-4">Scan with any UPI app to pay</p>
            
            <Dialog open={showPaymentForm} onOpenChange={setShowPaymentForm}>
              <DialogTrigger asChild>
                <Button 
                  className="mt-6 w-full"
                  style={{ backgroundColor: '#D4AF37', color: '#0F172A' }}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Submit Payment Screenshot
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Submit Payment</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label>Amount (₹) *</Label>
                    <Input 
                      type="number"
                      value={formData.amount}
                      onChange={(e) => setFormData({...formData, amount: e.target.value})}
                      placeholder="Enter amount"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label>Purpose *</Label>
                    <Input 
                      value={formData.purpose}
                      onChange={(e) => setFormData({...formData, purpose: e.target.value})}
                      placeholder="e.g., Membership Fee, Event Contribution"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label>Payment Date *</Label>
                    <Input 
                      type="date"
                      value={formData.payment_date}
                      onChange={(e) => setFormData({...formData, payment_date: e.target.value})}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label>Transaction ID (Optional)</Label>
                    <Input 
                      value={formData.transaction_id}
                      onChange={(e) => setFormData({...formData, transaction_id: e.target.value})}
                      placeholder="UPI/Bank Reference Number"
                    />
                  </div>
                  
                  <div>
                    <Label>Payment Screenshot *</Label>
                    <div className="mt-2">
                      {formData.screenshot_url ? (
                        <div className="relative">
                          <img 
                            src={getImageUrl(formData.screenshot_url)}
                            alt="Screenshot" 
                            className="w-full h-48 object-contain rounded-lg bg-muted"
                          />
                          <Button 
                            type="button"
                            size="sm"
                            variant="destructive"
                            className="absolute top-2 right-2"
                            onClick={() => setFormData({...formData, screenshot_url: ''})}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50">
                          <Upload className="w-8 h-8 mb-2 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">Click to upload</span>
                          <input 
                            type="file" 
                            accept="image/*" 
                            className="hidden" 
                            onChange={handleScreenshotUpload}
                            disabled={uploading}
                          />
                        </label>
                      )}
                    </div>
                  </div>
                  
                  <Button 
                    type="submit"
                    disabled={submitting || uploading || !formData.screenshot_url}
                    className="w-full"
                    style={{ backgroundColor: '#D4AF37', color: '#0F172A' }}
                  >
                    {submitting ? 'Submitting...' : 'Submit Payment'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      </div>
      
      {/* Payment History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5" style={{ color: '#D4AF37' }} />
            My Payment History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {myPayments.length === 0 ? (
            <div className="text-center py-8">
              <CreditCard className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
              <p className="text-muted-foreground">No payments submitted yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {myPayments.map((payment) => (
                <div key={payment.payment_id} className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                  <div>
                    <p className="font-semibold">₹{payment.amount.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">{payment.purpose}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(payment.payment_date)}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    payment.status === 'approved' ? 'bg-green-100 text-green-800' : 
                    payment.status === 'rejected' ? 'bg-red-100 text-red-800' : 
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div data-testid="payments-page">
      <MobileView />
      <DesktopView />
    </div>
  );
};

export default PaymentsPage;
